'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';

import WarehouseAddEdit from './WarehouseAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import { useWarehouseDataList, deleteWarehouse } from '../../../../stores/warehouseStore';
import { fleetApi } from '../../../../api/axiosClient';
import { useCompanyList } from '../../../../stores/companyStore';

function WarehousePageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('warehouse.name', 'Warehouse Name') },
      { value: 'warehouse_type', label: t('size.type', 'Type') },
      { value: 'location', label: t('dashboardAsset.location', 'Location') },
      { value: 'pic_name', label: t('masterCompany.picName', 'PIC Name') }
    ],
    [t]
  );

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const { companyOptions } = useCompanyList();

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }
    return [];
  }, [selectedCompanies]);

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('error');
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading,
    refetch
  } = useWarehouseDataList(paginationModel.page, paginationModel.pageSize, selectedCompanies, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [...companyFilterColumns];
      const fetchFilters = [];

      if (searchRaw && searchRaw.trim() !== '') {
        if (searchField === 'all') {
          fetchFilters.push({ name: 'name', value: searchRaw }, { name: 'name', table_name: 'm_company', value: searchRaw });
        } else {
          if (searchField === 'company') {
            fetchFilters.push({ name: 'name', table_name: 'm_company', logic_operator: 'like', operator: 'AND', value: searchRaw });
          } else {
            fetchFilters.push({ name: searchField, logic_operator: 'like', operator: 'AND', value: searchRaw });
          }
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [{ name: 'company', column_results: ['name'] }],
        columns: columns
      };

      if (fetchFilters.length > 0) {
        if (searchField === 'all') {
          payload.filters = fetchFilters;
        } else {
          payload.columns = [...payload.columns, ...fetchFilters];
        }
      }

      const resp = await fleetApi.post('/warehouse/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('warehouse export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteWarehouse(confirmTargetId);
      notify({ message: t('warehouse.notifSuccess', 'Warehouse successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('warehouse.notifFailed', 'Failed to delete Warehouse'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.5,
      valueGetter: (value, row) => row?.join?.company_name || '-'
    },
    { field: 'name', headerName: t('warehouse.name', 'Warehouse Name'), flex: 1.5 },
    {
      field: 'warehouse_type',
      headerName: t('size.type', 'Type'),
      flex: 1,
      valueFormatter: (value) => {
        const types = {
          warehouse: 'Warehouse',
          'repair-shop': 'Repair Shop',
          'scrap-yard': 'Scrap Yard',
          'retread-vendor': 'Retread Vendor'
        };
        return types[value] || value || '-';
      }
    },
    { field: 'location', headerName: t('dashboardAsset.location', 'Location'), flex: 1.5 },
    { field: 'pic_name', headerName: t('masterCompany.picName', 'PIC Name'), flex: 1 },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('warehouse.title', 'Master Warehouse')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('warehouse.search', 'Search Warehouse')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {openDialog && <WarehouseAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function WarehousePage() {
  return <WarehousePageContent />;
}
